<script setup lang="ts">
import { EButton, ETooltip, formatBinding, GLOBAL_SHORTCUTS } from '@erpx/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'

import AppIcon from '@/components/AppIcon.vue'
import AppLauncher from '@/components/AppLauncher.vue'
import AppTile from '@/components/AppTile.vue'
import QuickCreateMenu from '@/components/QuickCreateMenu.vue'
import LocaleMenu from '@/components/LocaleMenu.vue'
import ThemeMenu from '@/components/ThemeMenu.vue'
import UserMenu from '@/components/UserMenu.vue'
import { NAV_TOGGLE_ID, SIDENAV_ID, useCurrentApp, useNavPrefs } from '@/composables/useNavPrefs'
import { useOverlays } from '@/composables/useOverlays'
import { useRightRail } from '@/composables/useRightRail'
import { useUserScope } from '@/composables/useUserScope'
import NotificationBell from '@/modules/notifications/NotificationBell.vue'
import { appLandingPath } from '@/nav/apps'
import { useAuthStore } from '@/stores/auth'

/**
 * The command bar, start to end: who you are working for (brand mark → home),
 * where you can go (the app launcher), where you are (the current app), and
 * how to find anything (search); then the actions.
 *
 * The search field is a button, not an input, and that is deliberate: §11 wants
 * one global search that spans entities, actions and reports, and that is
 * exactly what the ⌘K palette is. Two search boxes that behave differently
 * would be a worse product than one that is always the same keystroke away.
 *
 * The brand mark moved here from the sidebar when the sidebar became
 * per-app: the home screen has no sidebar, and the way home has to be in the
 * same place on every screen, including the one it leads to.
 */

const auth = useAuthStore()
const overlays = useOverlays()
const rail = useRightRail()
const { t } = useI18n()

const userScope = useUserScope()
const prefs = useNavPrefs(userScope, auth.can)
const { current, onHomeScreen } = useCurrentApp(userScope, auth.can)

const paletteHint = computed(() => formatBinding(GLOBAL_SHORTCUTS.commandPalette))

const tenantInitials = computed(() => {
  const slug = auth.slug
  return slug === '' ? 'ER' : slug.slice(0, 2).toUpperCase()
})

const appLabel = computed(() => (current.value === undefined ? '' : t(current.value.app.labelKey)))
const appLanding = computed(() => (current.value === undefined ? '/' : appLandingPath(current.value.part)))
</script>

<template>
  <header class="topbar">
    <!-- Narrow screens only (styles.css): opens the current app's menu as a
         drawer. There is no menu to open on the home screen. -->
    <button
      v-if="!onHomeScreen"
      :id="NAV_TOGGLE_ID"
      type="button"
      class="topbar__menu"
      :aria-label="t('shell.topbar.appMenu', { app: appLabel })"
      :aria-expanded="prefs.drawerOpen.value"
      :aria-controls="SIDENAV_ID"
      @click="prefs.drawerOpen.value = !prefs.drawerOpen.value"
    >
      <AppIcon name="menu" :size="16" />
    </button>

    <RouterLink to="/" class="topbar__brand" :title="t('shell.topbar.goHome')">
      <span class="topbar__mark" aria-hidden="true">{{ tenantInitials }}</span>
      <span class="topbar__names">
        <span class="topbar__tenant">{{ auth.slug || t('app.shortName') }}</span>
        <span class="topbar__product">{{ t('app.shortName') }}</span>
      </span>
    </RouterLink>

    <span class="topbar__divider" aria-hidden="true" />

    <AppLauncher />

    <RouterLink
      v-if="!onHomeScreen && current"
      :to="appLanding"
      class="topbar__app"
      :title="t('shell.topbar.openAppHome', { app: appLabel })"
    >
      <AppTile :app="current.app" :size="22" />
      <span class="topbar__app-label">{{ appLabel }}</span>
    </RouterLink>

    <button
      type="button"
      class="topbar__search"
      :aria-label="t('shell.topbar.searchLabel')"
      @click="overlays.openPalette()"
    >
      <AppIcon name="search" :size="14" />
      <span class="topbar__search-text">{{ t('shell.topbar.searchPlaceholder') }}</span>
      <span class="kbd topbar__search-kbd">{{ paletteHint }}</span>
    </button>

    <span class="topbar__spacer" />

    <div class="topbar__actions">
      <QuickCreateMenu />

      <!-- CLAUDE.md §6: the bell. It renders nothing for a user without
           `notification.read`, and polls its own badge — see the component. -->
      <NotificationBell />

      <ETooltip :content="t('shell.topbar.shortcuts')" shortcut="?">
        <EButton
          variant="ghost"
          size="sm"
          icon-only
          :aria-label="t('shell.topbar.shortcuts')"
          @click="overlays.openCheatSheet()"
        >
          <template #icon><AppIcon name="help" :size="15" /></template>
        </EButton>
      </ETooltip>

      <ETooltip :content="t('shell.topbar.insights')">
        <EButton
          variant="ghost"
          size="sm"
          icon-only
          :aria-pressed="rail.open.value"
          :aria-label="t('shell.topbar.toggleInsights')"
          @click="rail.toggle()"
        >
          <template #icon><AppIcon name="panelRight" :size="15" /></template>
        </EButton>
      </ETooltip>

      <LocaleMenu />
      <ThemeMenu />
      <UserMenu />
    </div>
  </header>
</template>
